import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheck,
  faChevronDown,
  faInbox,
  faRotateRight,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { BetaTestReport, BetaTestReportRound, DeployTest } from '../../types';
import { API_BASE_URL } from '../../config/api';
import { BETA_PROGRAM, DeployProgram } from '../../config/deployProgram';
import DeployTestResultsOverview from './DeployTestResultsOverview';
import DeployTestActionItems, {
  ActionItemCreatePreset,
  descriptionFromAnswer,
  descriptionFromTask,
} from './DeployTestActionItems';

type ResultsView = 'overview' | 'results';

const formatDate = (value?: string | Date | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatAnswer = (value: string, type?: string) => {
  if (!value) return '—';
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

const formatPerson = (user?: BetaTestReport['user']) => {
  if (!user) return { name: 'Unknown tester', email: '' };
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return {
    name: name || user.email || 'Unknown tester',
    email: name && user.email ? user.email : '',
  };
};

const phoneTabLabel = (report: BetaTestReport) => {
  const name = report.phoneDeviceName || 'Phone';
  if (!report.phoneDevicePlatform) return name;
  const platform = report.phoneDevicePlatform === 'ios' ? 'iOS' : 'Android';
  return `${name} (${platform})`;
};

const phoneStatusClass = (report: BetaTestReport) => {
  if (report.status === 'not_received') return 'missing';
  if (report.status === 'rejected') return 'rejected';
  if (report.status === 'completed') return 'done';
  return 'progress';
};

const reportUserKey = (report: BetaTestReport) =>
  report.user?._id || report.user?.email || report._id;

const reportRounds = (report: BetaTestReport): BetaTestReportRound[] => {
  if (report.status === 'not_received') return [];
  const archived = report.rounds || [];
  const current: BetaTestReportRound = {
    round: report.round || Math.max(archived.length, 0) + 1,
    status: report.status,
    completedAt: report.completedAt,
    taskResponses: report.taskResponses || [],
    questionResponses: report.questionResponses || [],
  };
  return [...archived, current].sort((left, right) => left.round - right.round);
};

interface DeployTestResultsPageProps {
  program?: DeployProgram;
}

const DeployTestResultsPage: React.FC<DeployTestResultsPageProps> = ({ program = BETA_PROGRAM }) => {
  const { testId, reportId } = useParams<{ testId: string; reportId?: string }>();
  const navigate = useNavigate();
  const [test, setTest] = useState<DeployTest | null>(null);
  const [reports, setReports] = useState<BetaTestReport[]>([]);
  const [selectedRounds, setSelectedRounds] = useState<Record<string, number>>({});
  const [selectedPhones, setSelectedPhones] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retestingId, setRetestingId] = useState<string | null>(null);
  const [view, setView] = useState<ResultsView>(reportId ? 'results' : 'overview');
  const [actionItemPreset, setActionItemPreset] = useState<ActionItemCreatePreset | null>(null);

  const applyPayload = (payload: { test?: DeployTest | null; reports?: BetaTestReport[] }) => {
    const nextReports = payload.reports || [];
    setTest(payload.test || null);
    setReports(nextReports);
    setSelectedRounds((prev) => {
      const next = { ...prev };
      nextReports.forEach((report) => {
        const rounds = reportRounds(report);
        const latest = rounds[rounds.length - 1]?.round;
        if (latest && next[report._id] == null) {
          next[report._id] = latest;
        }
      });
      return next;
    });
    setSelectedPhones((prev) => {
      const next = { ...prev };
      nextReports.forEach((report) => {
        const userKey = reportUserKey(report);
        if (reportId && String(report._id) === String(reportId)) {
          next[userKey] = report._id;
          return;
        }
        if (!next[userKey]) next[userKey] = report._id;
      });
      return next;
    });
  };

  const load = useCallback(async () => {
    if (!testId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}${program.testsApi}/${testId}/reports`, {
        withCredentials: true,
      });
      applyPayload(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading test results:', err);
      setError(err.response?.data?.message || 'Failed to load test results');
    } finally {
      setLoading(false);
    }
  }, [program.testsApi, testId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (reportId) setView('results');
  }, [reportId]);

  const handleRetest = async (report: BetaTestReport) => {
    if (!testId || retestingId) return;
    setRetestingId(report._id);
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}${program.testsApi}/${testId}/reports/${report._id}/retest`,
        {},
        { withCredentials: true },
      );
      applyPayload(response.data);
      const nextRound = (report.round || 1) + 1;
      setSelectedRounds((prev) => ({ ...prev, [report._id]: nextRound }));
    } catch (err: any) {
      console.error('Error requesting retest:', err);
      setError(err.response?.data?.message || 'Failed to request retest');
    } finally {
      setRetestingId(null);
    }
  };

  const focusedReport = reportId
    ? reports.find((report) => String(report._id) === String(reportId))
    : null;
  const visibleReports = focusedReport
    ? reports.filter((report) => reportUserKey(report) === reportUserKey(focusedReport))
    : reports;

  const completed = visibleReports.filter((report) => report.status === 'completed');
  const rejected = visibleReports.filter((report) => report.status === 'rejected');
  const inProgress = visibleReports.filter((report) => report.status === 'in_progress');
  const notReceived = visibleReports.filter((report) => report.status === 'not_received');
  const canRetest = test?.status === 'testing';

  const testerViews = useMemo(() => {
    const groups = new Map<string, BetaTestReport[]>();
    visibleReports.forEach((report) => {
      const key = reportUserKey(report);
      const existing = groups.get(key) || [];
      existing.push(report);
      groups.set(key, existing);
    });

    return Array.from(groups.entries()).map(([userKey, phones]) => {
      const selectedId = selectedPhones[userKey] || phones[0]._id;
      const report = phones.find((phone) => phone._id === selectedId) || phones[0];
      const rounds = reportRounds(report);
      const selected = selectedRounds[report._id] || rounds[rounds.length - 1]?.round;
      return {
        userKey,
        phones,
        report,
        rounds,
        selected,
        current: rounds.find((round) => round.round === selected) || rounds[rounds.length - 1],
      };
    });
  }, [visibleReports, selectedPhones, selectedRounds]);

  return (
    <div className="container-fluid mt-3 mb-5 deploy-test-results-page">
      <div className="mb-3">
        <Link
          to={reportId && testId ? program.resultsPath(testId) : program.listPath}
          className="btn btn-sm btn-outline-secondary"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          {reportId ? 'All results' : 'Back'}
        </Link>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '180px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <div className="deploy-test-results-header">
            <div className="deploy-test-results-kicker">
              <FontAwesomeIcon icon={faInbox} />
              Results
            </div>
            <h1>{test?.name || 'Test results'}</h1>
            <div className="deploy-test-results-meta">
              {completed.length} completed
              {rejected.length > 0 ? ` · ${rejected.length} rejected` : ''}
              {inProgress.length > 0 ? ` · ${inProgress.length} in progress` : ''}
              {notReceived.length > 0 ? ` · ${notReceived.length} not started` : ''}
            </div>
          </div>

          {!reportId ? (
            <div className="deploy-test-page-tabs" role="tablist" aria-label="Results views">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'overview'}
                className={`deploy-test-page-tab${view === 'overview' ? ' is-active' : ''}`}
                onClick={() => setView('overview')}
              >
                Overview
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'results'}
                className={`deploy-test-page-tab${view === 'results' ? ' is-active' : ''}`}
                onClick={() => setView('results')}
              >
                Results
              </button>
            </div>
          ) : null}

          {reportId && !focusedReport ? (
            <div className="deploy-test-card-empty">This report was not found</div>
          ) : view === 'overview' && !reportId ? (
            <DeployTestResultsOverview
              test={test}
              reports={reports}
              program={program}
              onCreateActionItem={test ? (preset) => setActionItemPreset({
                ...preset,
                nonce: Date.now(),
              }) : undefined}
            />
          ) : reports.length === 0 ? (
            <div className="deploy-test-card-empty">No results yet</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {testerViews.map(({ userKey, phones, report, rounds, selected, current }) => {
                const person = formatPerson(report.user);
                const tasks = current?.taskResponses || [];
                const answers = current?.questionResponses || [];
                const doneCount = tasks.filter((task) => task.done).length;
                const statusClass = report.status === 'not_received'
                  ? 'missing'
                  : current?.status === 'rejected'
                    ? 'rejected'
                    : current?.status === 'completed'
                    ? 'done'
                    : 'progress';
                const statusLabel = report.status === 'not_received'
                  ? 'Not started'
                  : current?.status === 'rejected'
                    ? 'Rejected'
                    : current?.status === 'completed'
                    ? 'Completed'
                    : 'In progress';
                return (
                  <div key={userKey} className="card shadow-sm border-0 deploy-test-results-card">
                    <div className="card-body">
                      <div className="deploy-test-results-card-top">
                        <div>
                          <div className="deploy-test-results-name">{person.name}</div>
                          {person.email ? (
                            <div className="deploy-test-results-email">{person.email}</div>
                          ) : null}
                          <div className="deploy-test-results-status">
                            <span className={`deploy-test-results-pill is-${statusClass}`}>
                              {statusLabel}
                            </span>
                            {current?.completedAt ? (
                              <span>{formatDate(current.completedAt)}</span>
                            ) : null}
                          </div>
                        </div>
                        {canRetest && report.status === 'completed' && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleRetest(report)}
                            disabled={retestingId === report._id}
                          >
                            <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                            {retestingId === report._id ? 'Requesting…' : 'Request Retest'}
                          </button>
                        )}
                      </div>

                      {phones.length > 0 && (
                        <div className="deploy-test-phone-tabs" role="tablist" aria-label="Phones">
                          {phones.map((phone) => {
                            const phoneStatus = phoneStatusClass(phone);
                            const selected = phone._id === report._id;
                            return (
                              <button
                                key={phone._id}
                                type="button"
                                role="tab"
                                aria-selected={selected}
                                className={`deploy-test-phone-tab${selected ? ' is-active' : ''} is-${phoneStatus}`}
                                onClick={() => {
                                  setSelectedPhones((prev) => ({ ...prev, [userKey]: phone._id }));
                                  if (reportId && testId && !String(phone._id).startsWith('pending-')) {
                                    navigate(program.resultsReportPath(testId, phone._id), { replace: true });
                                  }
                                }}
                              >
                                <span className={`deploy-test-phone-tab-dot is-${phoneStatus}`} aria-hidden="true" />
                                {phoneTabLabel(phone)}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {rounds.length > 1 && (
                        <div className="deploy-test-round-tabs" role="tablist" aria-label="Rounds">
                          {rounds.map((round) => (
                            <button
                              key={`${report._id}-round-${round.round}`}
                              type="button"
                              className={`deploy-test-round-tab${selected === round.round ? ' is-active' : ''}`}
                              onClick={() => setSelectedRounds((prev) => ({ ...prev, [report._id]: round.round }))}
                            >
                              Round {round.round}
                            </button>
                          ))}
                        </div>
                      )}

                      {report.status === 'not_received' ? (
                        <div className="deploy-test-card-empty text-start">
                          This phone has not started the test yet.
                        </div>
                      ) : (
                      <details className="deploy-test-results-details">
                        <summary>
                          <span className="deploy-test-results-details-title">Questions and answers</span>
                          <span className="deploy-test-results-details-count">
                            {answers.length} question{answers.length === 1 ? '' : 's'}
                            {tasks.length > 0 ? ` · ${doneCount}/${tasks.length} tasks` : ''}
                          </span>
                          <FontAwesomeIcon icon={faChevronDown} className="deploy-test-results-details-caret" />
                        </summary>
                        <div className="deploy-test-results-details-body">
                          {tasks.length > 0 && (
                            <div className="deploy-test-results-block">
                              <div className="deploy-test-results-block-title">Tasks</div>
                              <ul className="deploy-test-results-tasks">
                                {tasks.map((task) => (
                                  <li
                                    key={`${report._id}-${selected}-${task.itemId}`}
                                    className={task.done ? 'is-done' : 'is-open'}
                                  >
                                    <FontAwesomeIcon icon={task.done ? faCheck : faXmark} />
                                    <span className="deploy-test-card-task-title">{task.text || 'Task'}</span>
                                    {test && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm deploy-test-card-task-action"
                                        onClick={() => setActionItemPreset({
                                          title: task.text || 'Untitled task',
                                          description: descriptionFromTask(test, program, {
                                            text: task.text || 'Untitled task',
                                            status: task.done ? 'passed' : 'testing',
                                            tester: person.name,
                                            phone: phoneTabLabel(report),
                                          }),
                                          nonce: Date.now(),
                                        })}
                                      >
                                        Create Action Item
                                      </button>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="deploy-test-results-block">
                            <div className="deploy-test-results-block-title">Answers</div>
                            {answers.length === 0 ? (
                              <div className="text-muted">No answers recorded.</div>
                            ) : (
                              <div className="deploy-test-results-answers">
                                {answers.map((answer) => (
                                  <div
                                    key={`${report._id}-${selected}-${answer.questionId}`}
                                    className="deploy-test-results-answer"
                                  >
                                    <div className="deploy-test-results-answer-main">
                                      <div className="deploy-test-results-question">{answer.prompt || 'Question'}</div>
                                      <div className="deploy-test-results-value">
                                        {formatAnswer(answer.value, answer.type)}
                                      </div>
                                      {answer.additionalInfo ? (
                                        <div className="deploy-test-results-note">{answer.additionalInfo}</div>
                                      ) : null}
                                    </div>
                                    {test && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm deploy-test-card-task-action"
                                        onClick={() => setActionItemPreset({
                                          title: `${answer.prompt || 'Question'} — ${person.name}`,
                                          description: descriptionFromAnswer(
                                            test,
                                            program,
                                            { prompt: answer.prompt || 'Question', type: answer.type },
                                            {
                                              tester: person.name,
                                              phone: phoneTabLabel(report),
                                              value: answer.value,
                                              additionalInfo: answer.additionalInfo,
                                            },
                                          ),
                                          nonce: Date.now(),
                                        })}
                                      >
                                        Create Action Item
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {test && (
        <DeployTestActionItems
          test={test}
          program={program}
          modalOnly
          createPreset={actionItemPreset}
          onActionItemsChange={(_, actionItems) => {
            setTest((current) => current ? { ...current, actionItems } : current);
          }}
        />
      )}
    </div>
  );
};

export default DeployTestResultsPage;
